import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Form, Button, Tabs, Tab } from 'react-bootstrap';
import {
  Search,
  RotateCcw,
  Film,
  Headphones,
  Image as ImageIcon,
  FileText,
} from 'lucide-react';
import { jubileeStorageService } from '../services/jubileeStorageService';
import JubileeVideoPlayerModal from '../viewers/JubileeVideoPlayerModal';
import JubileeImageLightbox from '../viewers/JubileeImageLightbox';
import JubileeDocumentViewerModal from '../viewers/JubileeDocumentViewerModal';

export default function JubileeGlobalSearch({ onPlayAudioTrack, initialQuery = '' }) {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState('all');

  const [activeTab, setActiveTab] = useState('all');

  // Modals state
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);

  const categories = useMemo(() => jubileeStorageService.getCategories(), []);
  const events = useMemo(() => jubileeStorageService.getEvents(), []);
  const allMedia = useMemo(() => jubileeStorageService.getAllMedia(), []);

  // Grouped search results
  const groupedResults = useMemo(() => {
    return jubileeStorageService.getGroupedSearchResults(searchQuery, {
      year: selectedYear,
      categoryId: selectedCategory,
      eventId: selectedEvent,
    });
  }, [searchQuery, selectedYear, selectedCategory, selectedEvent]);

  const handleClear = () => {
    setSearchQuery('');
    setSelectedYear('all');
    setSelectedCategory('all');
    setSelectedEvent('all');
  };

  const hasFilters = searchQuery !== '' || selectedYear !== 'all' || selectedCategory !== 'all' || selectedEvent !== 'all';
  const availableYears = Array.from(new Set(allMedia.map((m) => m.year))).sort((a, b) => b - a);

  return (
    <div className="py-4">
      <Container>
        {/* Search Header */}
        <div className="text-center mb-4 pb-3">
          <div className="p-3 bg-success bg-opacity-10 text-success rounded-circle d-inline-flex mb-2">
            <Search size={32} />
          </div>
          <h2 className="fw-bold text-slate-900" style={{ letterSpacing: '-0.5px' }}>
            MDU Platinum Jubilee Global Archive Search
          </h2>
          <p className="text-muted small mx-auto" style={{ maxWidth: '600px' }}>
            Search historical documents, audio interviews, photographs, decrees, and Jubilee film records across 75 years of archival heritage.
          </p>
        </div>

        {/* Big Search Box & Filters */}
        <div className="bg-white p-4 rounded-4 shadow-sm border mb-4">
          <div className="input-group input-group-lg mb-3">
            <span className="input-group-text bg-light border-end-0">
              <Search size={22} className="text-success" />
            </span>
            <Form.Control
              type="text"
              placeholder="Search across title, speaker, event, decree, author, keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-start-0 fs-6 shadow-none"
              autoFocus
            />
            {searchQuery && (
              <Button variant="outline-secondary" onClick={() => setSearchQuery('')}>
                Clear
              </Button>
            )}
          </div>

          <Row className="g-2 align-items-center">
            <Col xs={12} sm={4}>
              <Form.Select
                size="sm"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
              >
                <option value="all">Filter by Event (All)</option>
                {events.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} sm={3}>
              <Form.Select
                size="sm"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">Filter by Category (All)</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} sm={3}>
              <Form.Select
                size="sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="all">Filter by Year (All)</option>
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    Year {y}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} sm={2} className="text-end">
              {hasFilters && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClear}
                  className="w-100 d-flex align-items-center justify-content-center gap-1"
                >
                  <RotateCcw size={13} /> Reset Filters
                </Button>
              )}
            </Col>
          </Row>
        </div>

        {/* Search Results Summary */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold text-dark m-0">
            {groupedResults.totalCount} {groupedResults.totalCount === 1 ? 'Result' : 'Results'} Found
          </h5>
          <span className="text-muted small">
            Results categorized by media type
          </span>
        </div>

        {/* Separated Search Result Sections (Rule #11: Never mix all file types into one confusing result grid) */}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4 nav-pills custom-media-tabs"
        >
          {/* ALL SECTIONS COMBINED (BUT SEPARATED BY CATEGORY SECTIONS) */}
          <Tab eventKey="all" title={`All Media (${groupedResults.totalCount})`}>
            {/* 1. VIDEOS SECTION */}
            {groupedResults.videos.length > 0 && (
              <div className="mb-5">
                <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                  <Film size={20} className="text-danger" />
                  <h5 className="fw-bold text-slate-900 m-0">Videos ({groupedResults.videos.length})</h5>
                </div>
                <Row className="g-4">
                  {groupedResults.videos.map((vid) => (
                    <Col key={vid.id} xs={12} sm={6} lg={4}>
                      <div
                        className="jubilee-media-card cursor-pointer"
                        onClick={() => {
                          setSelectedVideo(vid);
                          setShowVideoModal(true);
                        }}
                      >
                        <div className="media-thumbnail-wrapper">
                          <img src={vid.thumbnailUrl} alt={vid.title} className="media-thumbnail-img" />
                          <span className="media-type-badge video">Video</span>
                          {vid.duration && <span className="media-duration-badge">{vid.duration}</span>}
                        </div>
                        <div className="media-card-body">
                          <h6 className="media-card-title">{vid.title}</h6>
                          <p className="media-card-desc">{vid.description}</p>
                          <span className="text-danger small fw-semibold">Watch Video →</span>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {/* 2. AUDIO SECTION */}
            {groupedResults.audios.length > 0 && (
              <div className="mb-5">
                <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                  <Headphones size={20} className="text-primary" />
                  <h5 className="fw-bold text-slate-900 m-0">Audio & Oral History ({groupedResults.audios.length})</h5>
                </div>
                <div className="d-flex flex-column gap-3">
                  {groupedResults.audios.map((aud) => (
                    <div key={aud.id} className="audio-card p-3">
                      <img src={aud.thumbnailUrl} alt={aud.title} className="audio-cover-art" />
                      <div className="flex-grow-1">
                        <div className="badge bg-primary bg-opacity-10 text-primary mb-1">{aud.year}</div>
                        <h6 className="fw-bold m-0">{aud.title}</h6>
                        <p className="text-muted small m-0">{aud.description}</p>
                        {aud.speaker && <div className="text-xs text-muted mt-1">Speaker: {aud.speaker}</div>}
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        className="rounded-pill px-3"
                        onClick={() => onPlayAudioTrack && onPlayAudioTrack(aud)}
                      >
                        Listen
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 3. IMAGES SECTION */}
            {groupedResults.images.length > 0 && (
              <div className="mb-5">
                <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                  <ImageIcon size={20} className="text-warning" />
                  <h5 className="fw-bold text-slate-900 m-0">Photographs ({groupedResults.images.length})</h5>
                </div>
                <Row className="g-4">
                  {groupedResults.images.map((img, idx) => (
                    <Col key={img.id} xs={12} sm={6} md={4}>
                      <div
                        className="jubilee-media-card cursor-pointer"
                        onClick={() => {
                          setSelectedImageIndex(idx);
                          setShowImageModal(true);
                        }}
                      >
                        <div className="media-thumbnail-wrapper" style={{ height: '200px' }}>
                          <img src={img.thumbnailUrl || img.fileUrl} alt={img.title} className="media-thumbnail-img" />
                          <span className="media-type-badge image">Photo</span>
                        </div>
                        <div className="media-card-body">
                          <h6 className="media-card-title">{img.title}</h6>
                          <p className="media-card-desc">{img.description}</p>
                          <span className="text-warning small fw-semibold">View Photo →</span>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {/* 4. DOCUMENTS SECTION */}
            {groupedResults.documents.length > 0 && (
              <div className="mb-5">
                <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                  <FileText size={20} className="text-success" />
                  <h5 className="fw-bold text-slate-900 m-0">Documents & Records ({groupedResults.documents.length})</h5>
                </div>
                <Row className="g-4">
                  {groupedResults.documents.map((doc) => (
                    <Col key={doc.id} xs={12} sm={6} md={4}>
                      <div
                        className="jubilee-media-card cursor-pointer"
                        onClick={() => {
                          setSelectedDoc(doc);
                          setShowDocModal(true);
                        }}
                      >
                        <div className="media-thumbnail-wrapper" style={{ height: '160px' }}>
                          <img src={doc.thumbnailUrl} alt={doc.title} className="media-thumbnail-img" />
                          <span className="media-type-badge document">Document</span>
                        </div>
                        <div className="media-card-body">
                          <h6 className="media-card-title">{doc.title}</h6>
                          <p className="media-card-desc">{doc.description}</p>
                          <span className="text-success small fw-semibold">Read Online →</span>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {groupedResults.totalCount === 0 && (
              <div className="text-center py-5 bg-white rounded-4 border my-4">
                <div className="p-3 bg-secondary bg-opacity-10 text-secondary rounded-circle d-inline-flex mb-3">
                  <Search size={36} />
                </div>
                <h5 className="fw-bold text-dark">No matching archival records found</h5>
                <p className="text-muted small mb-3">
                  Try searching with different terms such as 'Jubilee', '1952', 'Decree', 'Anthem', or 'Shembaganur'.
                </p>
                {hasFilters && (
                  <Button variant="outline-success" size="sm" onClick={handleClear}>
                    Reset Search
                  </Button>
                )}
              </div>
            )}
          </Tab>

          {/* DEDICATED VIDEOS TAB */}
          <Tab eventKey="videos" title={`Videos (${groupedResults.videos.length})`}>
            <Row className="g-4">
              {groupedResults.videos.map((vid) => (
                <Col key={vid.id} xs={12} sm={6} md={4}>
                  <div
                    className="jubilee-media-card cursor-pointer"
                    onClick={() => {
                      setSelectedVideo(vid);
                      setShowVideoModal(true);
                    }}
                  >
                    <div className="media-thumbnail-wrapper">
                      <img src={vid.thumbnailUrl} alt={vid.title} className="media-thumbnail-img" />
                    </div>
                    <div className="media-card-body">
                      <h6 className="media-card-title">{vid.title}</h6>
                      <span className="text-danger small">Watch Video →</span>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Tab>

          {/* DEDICATED AUDIOS TAB */}
          <Tab eventKey="audios" title={`Audio (${groupedResults.audios.length})`}>
            <div className="d-flex flex-column gap-3">
              {groupedResults.audios.map((aud) => (
                <div key={aud.id} className="audio-card p-3">
                  <img src={aud.thumbnailUrl} alt={aud.title} className="audio-cover-art" />
                  <div className="flex-grow-1">
                    <h6 className="fw-bold m-0">{aud.title}</h6>
                    <p className="text-muted small m-0">{aud.description}</p>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    className="rounded-pill"
                    onClick={() => onPlayAudioTrack && onPlayAudioTrack(aud)}
                  >
                    Listen
                  </Button>
                </div>
              ))}
            </div>
          </Tab>

          {/* DEDICATED IMAGES TAB */}
          <Tab eventKey="images" title={`Photos (${groupedResults.images.length})`}>
            <Row className="g-4">
              {groupedResults.images.map((img, idx) => (
                <Col key={img.id} xs={12} sm={6} md={4}>
                  <div
                    className="jubilee-media-card cursor-pointer"
                    onClick={() => {
                      setSelectedImageIndex(idx);
                      setShowImageModal(true);
                    }}
                  >
                    <div className="media-thumbnail-wrapper" style={{ height: '200px' }}>
                      <img src={img.thumbnailUrl || img.fileUrl} alt={img.title} className="media-thumbnail-img" />
                    </div>
                    <div className="media-card-body">
                      <h6 className="media-card-title">{img.title}</h6>
                      <span className="text-warning small">View Photo →</span>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Tab>

          {/* DEDICATED DOCUMENTS TAB */}
          <Tab eventKey="documents" title={`Documents (${groupedResults.documents.length})`}>
            <Row className="g-4">
              {groupedResults.documents.map((doc) => (
                <Col key={doc.id} xs={12} sm={6} md={4}>
                  <div
                    className="jubilee-media-card cursor-pointer"
                    onClick={() => {
                      setSelectedDoc(doc);
                      setShowDocModal(true);
                    }}
                  >
                    <div className="media-thumbnail-wrapper" style={{ height: '160px' }}>
                      <img src={doc.thumbnailUrl} alt={doc.title} className="media-thumbnail-img" />
                    </div>
                    <div className="media-card-body">
                      <h6 className="media-card-title">{doc.title}</h6>
                      <span className="text-success small">Read Online →</span>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Tab>
        </Tabs>
      </Container>

      {/* Media Preview Modals */}
      <JubileeVideoPlayerModal
        video={selectedVideo}
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        allVideos={allMedia.filter((m) => m.mediaType === 'video')}
      />

      <JubileeImageLightbox
        images={groupedResults.images}
        selectedIndex={selectedImageIndex}
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        onSelectIndex={(i) => setSelectedImageIndex(i)}
      />

      <JubileeDocumentViewerModal
        document={selectedDoc}
        show={showDocModal}
        onHide={() => setShowDocModal(false)}
      />
    </div>
  );
}
